import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  ClientBudgetContext,
  ClientBudgetContextType,
  UpdateMonthlyBudgetResponse,
} from "./ClientBudgetContext";

import { campaignJobBoardQueryKeys } from "@/features/campaigns/jobboards/api/queryKeys";
import { clientQueryKeys } from "@/features/clients/api/query-keys";
import { invoiceQueryKeys } from "@/features/invoices/api/queryKeys";
import { instance } from "@/lib/axios/instance";
import useToast from "@/lib/hooks/useToast";
import { ClientType } from "@/types/client/ClientType";
import { CreditBudgetResponse } from "@/types/response/CreditBudgetResponse";
import { DebitBudgetResponse } from "@/types/response/DebitBudgetResponse";
import { ValidationErrorResponse } from "@/types/response/ValidationResponse";

interface ClientBudgetProviderProps {
  children: ReactNode;
  clientId: string;
}

export interface UpdateMonthlyBudgetPayload {
  clientId: string;
  payload: {
    amount: number;
  };
}

export interface UpdateAllowOverspendPayload {
  clientId: string;
  payload: {
    allowOverspend: boolean;
  };
}

export interface DebitBudgetPayload {
  clientId: string;
  payload: {
    amount: number;
    comments: string;
  };
}

export interface CreditBudgetPayload {
  clientId: string;
  payload: {
    amount: number;
    comments: string;
  };
}

const updateMonthlyBudgetApi = async (
  variables: UpdateMonthlyBudgetPayload
): Promise<AxiosResponse<UpdateMonthlyBudgetResponse>> => {
  const { clientId, payload } = variables;
  return instance.post<UpdateMonthlyBudgetResponse>(
    `/api/v2/Clients/${clientId}/Budget/Monthly`,
    { ...payload }
  );
};

const updateAllowOverspendApi = async (
  variables: UpdateAllowOverspendPayload
): Promise<AxiosResponse<void>> => {
  const { clientId, payload } = variables;
  return instance.put<void>(
    `/api/v2/Clients/${clientId}/Budget/AllowOverspend`,
    { ...payload }
  );
};

const creditBudgetApi = async (
  variables: CreditBudgetPayload
): Promise<AxiosResponse<CreditBudgetResponse>> => {
  const { clientId, payload } = variables;
  return instance.post<CreditBudgetResponse>(
    `/api/v2/Clients/${clientId}/Budget/Credit`,
    payload
  );
};

const debitBudgetApi = async (
  variables: DebitBudgetPayload
): Promise<AxiosResponse<DebitBudgetResponse>> => {
  const { clientId, payload } = variables;
  return instance.post<DebitBudgetResponse>(
    `/api/v2/Clients/${clientId}/Budget/Debit`,
    payload
  );
};

export const ClientBudgetProvider = ({
  children,
  clientId,
}: ClientBudgetProviderProps) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const updateMonthlyBudget = useMutation<
    AxiosResponse<UpdateMonthlyBudgetResponse>,
    AxiosError<ValidationErrorResponse>,
    { amount: number },
    { previousClient?: ClientType }
  >({
    mutationFn: async ({ amount }: { amount: number }) =>
      updateMonthlyBudgetApi({ clientId, payload: { amount } }),
    onMutate: async ({ amount }) => {
      const detailKey = clientQueryKeys.detail(clientId);

      await queryClient.cancelQueries({ queryKey: detailKey });

      const previousClient =
        queryClient.getQueryData<ClientType>(detailKey);

      if (previousClient) {
        queryClient.setQueryData<ClientType>(detailKey, {
          ...previousClient,
          monthlyBudget: amount,
        });
      }

      return { previousClient };
    },
    onError: (_, __, context) => {
      if (context?.previousClient) {
        queryClient.setQueryData(
          clientQueryKeys.detail(clientId),
          context.previousClient
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: clientQueryKeys.detail(clientId),
      });
      queryClient.invalidateQueries({
        queryKey: campaignJobBoardQueryKeys.clientBudgetMonth.byClient(clientId),
        exact: false,
      });
    },
  });

  const updateAllowOverspend = useMutation<
    AxiosResponse<void>,
    AxiosError<ValidationErrorResponse>,
    { allowOverspend: boolean }
  >({
    mutationFn: async ({ allowOverspend }: { allowOverspend: boolean }) =>
      updateAllowOverspendApi({ clientId, payload: { allowOverspend } }),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: clientQueryKeys.detail(clientId),
      });
    },
  });

  const debitBudget = useMutation<
    AxiosResponse<DebitBudgetResponse>,
    AxiosError<ValidationErrorResponse>,
    DebitBudgetPayload["payload"]
  >({
    mutationFn: async ({ amount, comments }: DebitBudgetPayload["payload"]) =>
      debitBudgetApi({ clientId, payload: { amount, comments } }),
    onSuccess: () => {
      showToast(
        t("success.save", { entity: t("common.transactions") }),
        "success"
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.cache.transactions.byClient(clientId),
      });
      queryClient.invalidateQueries({
        queryKey: clientQueryKeys.detail(clientId),
      });
    },
  });

  const creditBudget = useMutation<
    AxiosResponse<CreditBudgetResponse>,
    AxiosError<ValidationErrorResponse>,
    CreditBudgetPayload["payload"]
  >({
    mutationFn: async ({ amount, comments }: CreditBudgetPayload["payload"]) =>
      creditBudgetApi({ clientId, payload: { amount, comments } }),
    onSuccess: () => {
      showToast(
        t("success.save", { entity: t("common.transactions") }),
        "success"
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.cache.transactions.byClient(clientId),
      });
      queryClient.invalidateQueries({
        queryKey: clientQueryKeys.detail(clientId),
      });
    },
  });

  const value: ClientBudgetContextType = {
    creditBudget,
    debitBudget,
    error: updateMonthlyBudget.error,
    isLoading: updateMonthlyBudget.isPending,
    updateAllowOverspend,
    updateMonthlyBudget,
  };

  return (
    <ClientBudgetContext.Provider value={value}>
      {children}
    </ClientBudgetContext.Provider>
  );
};
